import { Stack, Typography } from '@mui/material';
import {
  AttendanceAggregateTimetableStructureResponse,
  AttendanceCode,
  JustificationBase,
} from '@schooly/api';
import { DropdownSelect, DropdownSelectRow } from '@schooly/components/filters';
import { FC, useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { AttendanceGridStudent, AttendanceSidebarFocus } from '../common/types';
import { AttendanceSidebarEntry } from './AttendanceSidebarEntry';
import {
  filterEntryByStructure,
  getFocusedStructureId,
  isPeriodFocused,
  SidebarEntry,
} from './utils';

type AttendanceSidebarPeriodsProps = {
  structures: AttendanceAggregateTimetableStructureResponse[];
  entries: SidebarEntry[];
  student: AttendanceGridStudent;
  justification: JustificationBase | null;
  codesById: Record<string, AttendanceCode>;
  date: string;
  focus?: AttendanceSidebarFocus;
  disabled?: boolean;
};

export const AttendanceSidebarPeriods: FC<AttendanceSidebarPeriodsProps> = ({
  structures,
  entries,
  student,
  justification,
  codesById,
  date,
  focus,
  disabled,
}) => {
  const { formatMessage } = useIntl();
  const dropdown = useRef<DropdownSelect | null>(null);
  const initialStructureId = (focus && getFocusedStructureId(entries, focus)) ?? structures[0]?.id;
  const [structureId, setStructureId] = useState(initialStructureId);
  const selectedStructure = structures.find(({ id }) => id === structureId);
  const periodEntries = entries.reduce<SidebarEntry[]>((acc, entry) => {
    if (!structureId) return acc;

    const filtered = filterEntryByStructure(entry, structureId);
    if (filtered) acc.push(filtered);

    return acc;
  }, []);

  const handleSelect = useCallback((id: string) => {
    setStructureId(id);
    dropdown.current?.close();
  }, []);

  const renderContent = useCallback(
    () => (
      <Stack gap={0.25} p={1}>
        {structures.map((structure) => (
          <DropdownSelectRow
            key={structure.id}
            isSelected={structure.id === structureId}
            onClick={() => handleSelect(structure.id)}
          >
            {structure.name}
          </DropdownSelectRow>
        ))}
      </Stack>
    ),
    [handleSelect, structureId, structures],
  );

  return (
    <Stack gap={2}>
      {structures.length > 1 && (
        <DropdownSelect
          ref={dropdown}
          label={formatMessage({ id: 'school-sections-TimetableStructure' })}
          hasValues={!!selectedStructure}
          renderContent={renderContent}
        >
          {() => (
            <Typography variant="h3" color="primary.main" noWrap>
              {selectedStructure?.name}
            </Typography>
          )}
        </DropdownSelect>
      )}

      {periodEntries.length > 0 &&
        periodEntries.map((entry) => (
          <AttendanceSidebarEntry
            key={entry.periods[0]?.lesson.id}
            entry={entry}
            student={student}
            justification={justification}
            codesById={codesById}
            date={date}
            collapsible={periodEntries.length > 1}
            defaultExpanded={!!focus && isPeriodFocused(focus, entry)}
            disabled={disabled}
          />
        ))}
    </Stack>
  );
};
